/* Number/date formatting helpers — Austrian locale by default. */
export function formatEUR(value, fractionDigits = 2) {
  if (value === null || value === undefined || isNaN(Number(value))) return "—";
  return new Intl.NumberFormat("de-AT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(Number(value));
}

export function formatNumber(value, fractionDigits = 2) {
  if (value === null || value === undefined || isNaN(Number(value))) return "—";
  return new Intl.NumberFormat("de-AT", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(Number(value));
}

export function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

export function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
}

/* Parses a German/Austrian decimal string ("12,50") to a number (12.5). */
export function parseATDecimal(str) {
  if (typeof str === "number") return str;
  if (!str) return 0;
  const cleaned = String(str).replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

/**
 * Returns human-friendly per-unit price breakdowns. ppu is in EUR per base unit.
 *  - g  → [{u:"g", v:ppu}, {u:"kg", v:ppu*1000}]
 *  - kg → [{u:"kg", v:ppu}, {u:"g", v:ppu/1000}]
 *  - ml → [{u:"ml", v:ppu}, {u:"cl", v:ppu*10}, {u:"L", v:ppu*1000}]
 *  - L  → [{u:"L", v:ppu}, {u:"cl", v:ppu*10}, {u:"ml", v:ppu/1000}]
 *  - Stueck/Portion → [{u:"Stück" / "Portion", v:ppu}]
 */
export function pricePerUnitBreakdown(ppu, baseUnit) {
  if (ppu == null || isNaN(Number(ppu))) return [];
  const p = Number(ppu);
  switch (baseUnit) {
    case "g":
      return [
        { u: "g", v: p, digits: 4 },
        { u: "kg", v: p * 1000, digits: 2 },
      ];
    case "kg":
      return [
        { u: "kg", v: p, digits: 2 },
        { u: "g", v: p / 1000, digits: 4 },
      ];
    case "ml":
      return [
        { u: "ml", v: p, digits: 4 },
        { u: "cl", v: p * 10, digits: 3 },
        { u: "L", v: p * 1000, digits: 2 },
      ];
    case "cl":
      return [
        { u: "cl", v: p, digits: 3 },
        { u: "ml", v: p / 10, digits: 4 },
        { u: "L", v: p * 100, digits: 2 },
      ];
    case "L":
      return [
        { u: "L", v: p, digits: 2 },
        { u: "cl", v: p / 100, digits: 3 },
        { u: "ml", v: p / 1000, digits: 4 },
      ];
    case "Stueck":
      return [{ u: "Stück", v: p, digits: 2 }];
    case "Portion":
      return [{ u: "Portion", v: p, digits: 2 }];
    default:
      return [{ u: baseUnit, v: p, digits: 2 }];
  }
}

export const CATEGORY_LABELS = {
  Fleisch: "Fleisch",
  Fisch: "Fisch",
  Gemuese: "Gemüse",
  Obst: "Obst",
  Milchprodukte: "Milchprodukte",
  Spirituosen: "Spirituosen",
  Wein: "Wein",
  Bier: "Bier",
  Softdrinks: "Softdrinks",
  Gewuerze: "Gewürze",
  Sonstiges: "Sonstiges",
};

export const RECIPE_CATEGORY_LABELS = {
  Vorspeise: "Vorspeise",
  Suppe: "Suppe",
  Hauptgang: "Hauptgang",
  Dessert: "Dessert",
  Snack: "Snack",
  Fruehstueck: "Frühstück",
  Vegetarisch: "Vegetarisch",
  Vegan: "Vegan",
  Saisonal: "Saisonal",
};

export const UNIT_LABELS = {
  g: "g", kg: "kg", ml: "ml", cl: "cl", L: "L",
  Stueck: "Stück", Portion: "Portion",
  dash: "Dash", splash: "Splash",
};

export const COCKTAIL_CATEGORY_LABELS = {
  Aperitif: "Aperitif",
  Spritz: "Spritz",
  Sour: "Sour",
  Fruchtig: "Fruchtig",
  Afterdinner: "Afterdinner",
  Longdrink: "Longdrink",
  Alkoholfrei: "Alkoholfrei",
  Seasonal: "Seasonal",
  Signature: "Signature",
};

export const ICE_TYPE_LABELS = {
  none: "Kein Eis",
  cubed: "Würfel",
  crushed: "Crushed",
  block: "Block",
  sphere: "Kugel",
  shaved: "Shaved",
};

export const ALLERGEN_ABBR = {
  gluten: "Gl",
  krebstiere: "Kr",
  eier: "Ei",
  fisch: "Fi",
  erdnuesse: "Er",
  soja: "So",
  milch: "Mi",
  schalenfruechte: "Nü",
  sellerie: "Se",
  senf: "Sf",
  sesam: "Sm",
  schwefeldioxid: "SO₂",
  lupinen: "Lu",
  weichtiere: "We",
};
